import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { HelmetProvider } from "react-helmet-async";
import { I18nextProvider } from "react-i18next";
import { AuthProvider } from "./contexts/AuthContext";
import theme from "./styles/theme";
import i18n from "./i18n/i18n";
import MainLayout from "./layouts/MainLayout";
import AdminLayout from "./layouts/AdminLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import SimpleAccessibility from "./components/SimpleAccessibility";
import AriaLabels from "./components/AriaLabels";
import PageTitle from "./components/PageTitle";
import Home from "./pages/Home";
import Workshops from "./pages/Workshops";
import WorkshopDetail from "./pages/WorkshopDetail";
import About from "./pages/About";
import AboutDetail from "./pages/AboutDetail";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import BlogList from "./pages/BlogList";
import Gallery from "./pages/Gallery";
import GalleryDetail from "./pages/GalleryDetail";
import ThankYou from "./pages/ThankYou";
import Payment from "./pages/Payment";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentFailed from "./pages/PaymentFailed";
import GiftVoucher from "./pages/GiftVoucher";
import VoucherPayment from "./pages/VoucherPayment";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AccessibilityStatement from "./pages/AccessibilityStatement";
import CancellationPolicy from "./pages/CancellationPolicy";
import NotFound from "./pages/NotFound";
import Login from "./pages/admin/Login";
import Dashboard from "./pages/admin/Dashboard";
import AdminWorkshops from "./pages/admin/Workshops";
import AdminBlog from "./pages/admin/Blog";
import AdminMedia from "./pages/admin/Media";
import AdminSettings from "./pages/admin/Settings";
import AdminContact from "./pages/admin/Contact";
import AdminRegistrations from "./pages/admin/Registrations";
import AdminGiftVoucher from "./pages/admin/GiftVoucher";

const App: React.FC = () => {
  // הגדרת כיוון הטקסט בטעינה הראשונית
  React.useEffect(() => {
    const currentLang = i18n.language || "he";
    document.dir = currentLang === "he" ? "rtl" : "ltr";

    // Update body class
    document.body.classList.remove("rtl", "ltr");
    document.body.classList.add(currentLang === "he" ? "rtl" : "ltr");
  }, []);

  return (
    <HelmetProvider>
      <SimpleAccessibility>
        <AriaLabels />
        <I18nextProvider i18n={i18n}>
          <ThemeProvider theme={theme}>
            <Router>
              <PageTitle />
              <AuthProvider>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<MainLayout />}>
                  <Route index element={<Home />} />
                  <Route path="workshops" element={<Workshops />} />
                  <Route path="workshops/:id" element={<WorkshopDetail />} />
                  <Route path="about" element={<About />} />
                  <Route path="about-detail" element={<AboutDetail />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="blog" element={<Blog />} />
                  <Route path="blog-detail" element={<BlogList />} />
                  <Route path="blog/:id" element={<BlogDetail />} />
                  <Route path="gallery" element={<Gallery />} />
                  <Route path="gallery-detail" element={<GalleryDetail />} />
                  <Route path="payment/:registrationId" element={<Payment />} />
                  <Route path="payment/success" element={<PaymentSuccess />} />
                  <Route path="payment/failed" element={<PaymentFailed />} />
                  <Route path="gift-voucher" element={<GiftVoucher />} />
                  <Route
                    path="voucher-payment/:voucherId"
                    element={<VoucherPayment />}
                  />
                  <Route path="thank-you" element={<ThankYou />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route
                    path="accessibility"
                    element={<AccessibilityStatement />}
                  />
                  <Route
                    path="cancellation-policy"
                    element={<CancellationPolicy />}
                  />
                </Route>

                {/* Admin Routes */}
                <Route path="/admin/login" element={<Login />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="workshops" element={<AdminWorkshops />} />
                  <Route path="blog" element={<AdminBlog />} />
                  <Route path="media" element={<AdminMedia />} />
                  <Route path="contact" element={<AdminContact />} />
                  <Route
                    path="registrations"
                    element={<AdminRegistrations />}
                  />
                  <Route
                    path="gift-voucher"
                    element={<AdminGiftVoucher />}
                  />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>

                {/* 404 Route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AuthProvider>
            </Router>
          </ThemeProvider>
        </I18nextProvider>
      </SimpleAccessibility>
    </HelmetProvider>
  );
};

export default App;
